/**
 * Theme Provider
 * React Context provider that detects domain and applies the appropriate theme
 */

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { getThemeForDomain, detectDomain, getThemeKey } from '../services/themeService';
import { themeToCSSVariables } from './themeConfig';

// Create Theme Context
const ThemeContext = createContext(null);

/**
 * Custom hook to access theme context
 * @returns {Object} Theme context value
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider Component
 * Detects domain, selects theme, and injects CSS variables
 */
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [themeKey, setThemeKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Detect domain and get appropriate theme
    const domain = detectDomain();
    const theme = getThemeForDomain(domain);
    const key = getThemeKey(domain);
    
    setCurrentDomain(domain);
    setCurrentTheme(theme);
    setThemeKey(key);
    
    // Convert theme to CSS variables
    const cssVariables = themeToCSSVariables(theme);
    
    // Inject CSS variables into :root
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Add data attribute for debugging and CSS selectors
    root.setAttribute('data-theme', key);
    
    // Log theme application (development mode)
    if (process.env.NODE_ENV === 'development') {
      console.log('🎨 Theme Applied:', {
        domain,
        themeName: theme.name,
        themeKey: key,
      });
    }
    
    setIsLoading(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      theme: currentTheme,
      themeName: currentTheme?.name,
      themeKey,
      domain: currentDomain,
      isLoading,
    }),
    [currentTheme, currentDomain, themeKey, isLoading]
  );

  // Render children immediately (theme is applied synchronously)
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
